import { useLazyQuery, useMutation } from "@apollo/client";
import { onErrorGraphql } from "utils/onErrorGraphql";
import { clientNeoFact } from "@neonumy/api-clients";
import { FormInstance, Typography } from "antd";
import {
  ACTIVATE_VAT,
  CREATE_VAT,
  DELETE_VAT,
  GET_VAT,
  LIST_VATS,
  UPDATE_VAT,
} from "queries/neoFact/vats";
import { useEffect, useMemo } from "react";
import useSettingsStore from "views/hooks/useSettingsStore";

const { Text } = Typography;

export default function useVatsApi(id?: ID, form?: FormInstance<any>) {
  const setCompanyRates = useSettingsStore(state => state.setCompanyRates);
  const setActiveVats = useSettingsStore(state => state.setActiveVats);

  const [
    listVats,
    {
      loading: listVatsLoading,
      refetch: listVatsRefetch,
      data: listVatsQueryData,
      error: listVatsError,
    },
  ] = useLazyQuery<ListVatsResponse, ListVatsVars>(LIST_VATS, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (listVatsError) onErrorGraphql(listVatsError);
  }, [listVatsError]);

  const listVatsData: ListVatsResults = useMemo(
    () =>
      listVatsQueryData?.listVats?.results?.map(item => ({
        ...item,
        key: item.id,
      })) ?? [],
    [listVatsQueryData],
  );

  const listVatsTotalCount = listVatsQueryData?.listVats?.totalCount ?? 0;

  useEffect(() => {
    const results = listVatsQueryData?.listVats?.results;
    if (!results) return;

    const newRates = results.map(item => ({
      label: (
        <>
          <Text>{item?.ratePercentage} %</Text>
          <br />
          <Text>
            <small>
              {item?.code} - {item?.name}
            </small>
          </Text>
        </>
      ),
      value: item.id,
      rate: Number(item.ratePercentage),
    }));

    setCompanyRates(newRates);
    setActiveVats(newRates);
  }, [listVatsQueryData]);

  const [createUpdateVat, { loading: createUpdateVatLoading }] = useMutation<
    CreateUpdateVatMutationResponse,
    CreateVatVars | UpdateVatVars
  >(id ? UPDATE_VAT : CREATE_VAT, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  const [deleteVat] = useMutation<DeleteVatResponse, DeleteVatVars>(DELETE_VAT, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });
  const [activateVat] = useMutation<ActivateVatResponse, ActivateVatVars>(ACTIVATE_VAT, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  const [getVat, { refetch: refetchGetVat, data: getVatData, error: getVatError }] = useLazyQuery<
    GetVatResponse,
    GetVarTypeID
  >(GET_VAT, {
    client: clientNeoFact,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (getVatError) onErrorGraphql(getVatError);
  }, [getVatError]);

  useEffect(() => {
    const getVat = getVatData?.getVat;
    if (!getVat) return;

    form?.setFields([
      { name: "code", value: getVat?.code },
      { name: "rate", value: getVat?.ratePercentage },
      { name: "name", value: getVat?.name },
    ]);
  }, [getVatData, form]);

  return {
    getVat,
    activateVat,
    listVats,
    deleteVat,
    listVatsLoading,
    listVatsRefetch,
    refetchGetVat,
    createUpdateVat,
    listVatsData,
    createUpdateVatLoading,
    listVatsTotalCount,
  };
}
